import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTheme } from "./ThemeProvider";

export default function DashboardShowcase({
  currentView = 0,
  autoRotate = true,
  rotationInterval = 8000,
}) {
  const { isDark } = useTheme();
  const [activeView, setActiveView] = useState(currentView);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 50, y: 50 });
  const [isClicking, setIsClicking] = useState(false);
  const [imageScale, setImageScale] = useState(1);
  const imageContainerRef = useRef(null);
  const animationTimeoutRef = useRef(null);

  const imageSets = {
    light: [
      "/img/AssistantBright.png",
      "/img/AssistantBright1.png",
      "/img/AssistantBright2.png",
      "/img/AssistantBright3.png",
      "/img/AssistantBright4.png",
    ],
    dark: [
      "/img/AssistantDark.png",
      "/img/AssistantDark1.png",
      "/img/AssistantDark2.png",
      "/img/AssistantDark3.png",
      "/img/AssistantDark4.png",
    ],
  };

  const currentImages = isDark ? imageSets.dark : imageSets.light;
  const totalViews = currentImages.length;

  const clickPositions = useMemo(
    () => [
      { x: 12, y: 25 },
      { x: 75, y: 45 },
      { x: 85, y: 20 },
      { x: 50, y: 60 },
      { x: 50, y: 50 },
    ],
    []
  );

  useEffect(() => {
    if (!autoRotate) return;

    // Clear any existing timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    setShowCursor(false);
    setIsClicking(false);
    setImageScale(1);

    const sequence = () => {
      setImageScale(1.05);

      setTimeout(() => {
        setShowCursor(true);
        setCursorPosition({ x: 30, y: 30 });

        setTimeout(() => {
          setCursorPosition(clickPositions[activeView]);

          setTimeout(() => {
            setIsClicking(true);

            setTimeout(() => {
              setTimeout(() => {
                setIsClicking(false);
                setShowCursor(false);
                setIsTransitioning(true);
                setImageScale(1);

                setTimeout(() => {
                  setActiveView((prev) => (prev + 1) % totalViews);
                  setIsTransitioning(false);
                }, 500);
              }, 500);
            }, 400);
          }, 1200);
        }, 800);
      }, 600);
    };

    animationTimeoutRef.current = setTimeout(sequence, 1200);

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [activeView, autoRotate, totalViews, clickPositions]);

  useEffect(() => {
    if (
      currentView !== activeView &&
      currentView >= 0 &&
      currentView < totalViews
    ) {
      setShowCursor(false);
      setIsClicking(false);
      setIsTransitioning(true);
      setImageScale(1);
      setTimeout(() => {
        setActiveView(currentView);
        setIsTransitioning(false);
      }, 500);
    }
  }, [currentView, totalViews]);

  const goToView = (index) => {
    if (index >= 0 && index < totalViews && index !== activeView) {
      setShowCursor(false);
      setIsClicking(false);
      setIsTransitioning(true);
      setImageScale(1);
      setTimeout(() => {
        setActiveView(index);
        setIsTransitioning(false);
      }, 500);
    }
  };

  const nextView = () => {
    goToView((activeView + 1) % totalViews);
  };

  const prevView = () => {
    goToView((activeView - 1 + totalViews) % totalViews);
  };

  return (
    <section
      id="dashboard-showcase"
      className="relative w-full py-16 lg:py-24 overflow-hidden"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
            Powerful <span className="text-accent">Dashboard</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            Experience our intuitive interface designed for seamless AI
            assistant creation and management
          </p>
        </div>

        {/* Dashboard Image Container */}
        <div className="relative max-w-7xl mx-auto" ref={imageContainerRef}>
          <div className="relative w-full aspect-video rounded-2xl lg:rounded-3xl overflow-hidden bg-background/50 backdrop-blur-sm border border-border/50 shadow-2xl">
            <div className="relative w-full h-full">
              {currentImages.map((imagePath, index) => (
                <img
                  key={`${isDark ? "dark" : "light"}-${index}`}
                  src={imagePath}
                  alt={`Dashboard view ${index + 1}`}
                  className={`absolute inset-0 w-full h-full object-contain ${
                    index === activeView ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                  style={{
                    transition:
                      "opacity 1s cubic-bezier(0.4, 0, 0.2, 1), transform 1s cubic-bezier(0.4, 0, 0.2, 1)",
                    transform:
                      index === activeView
                        ? `scale(${imageScale})`
                        : "scale(1)",
                  }}
                />
              ))}
            </div>

            {/* Animated Cursor */}
            {showCursor && (
              <div
                className="absolute z-40 pointer-events-none"
                style={{
                  left: `${cursorPosition.x}%`,
                  top: `${cursorPosition.y}%`,
                  transform: `translate(-50%, -50%) ${
                    isClicking ? "scale(0.85)" : "scale(1)"
                  }`,
                  transition:
                    "left 1.2s cubic-bezier(0.4, 0, 0.2, 1), top 1.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease-out",
                }}
              >
                <div className="relative">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="drop-shadow-2xl"
                    style={{
                      filter:
                        "drop-shadow(0 4px 8px rgba(0,0,0,0.4)) drop-shadow(0 0 8px rgba(22, 163, 74, 0.3))",
                    }}
                  >
                    <path
                      d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z"
                      fill="white"
                      stroke="#16a34a"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {isClicking && (
                    <>
                      <div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border-2 border-accent opacity-60"
                        style={{
                          animation: "cursorClickRipple 0.5s ease-out",
                        }}
                      />
                      <div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-accent/20"
                        style={{
                          animation: "cursorClickPulse 0.5s ease-out",
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            )}

            {isTransitioning && (
              <div
                className="absolute inset-0 bg-background/10 backdrop-blur-[1px] z-20 pointer-events-none"
                style={{
                  animation: "fadeInOut 1s ease-in-out",
                }}
              />
            )}

            <button
              onClick={prevView}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-background/80 dark:bg-background/60 backdrop-blur-sm border border-border/50 hover:border-accent/50 text-foreground hover:text-accent transition-all duration-300 hover:scale-110 shadow-lg flex items-center justify-center group"
              aria-label="Previous view"
            >
              <svg
                className="w-6 h-6 transition-transform group-hover:-translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={nextView}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-background/80 dark:bg-background/60 backdrop-blur-sm border border-border/50 hover:border-accent/50 text-foreground hover:text-accent transition-all duration-300 hover:scale-110 shadow-lg flex items-center justify-center group"
              aria-label="Next view"
            >
              <svg
                className="w-6 h-6 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <div className="flex justify-center items-center gap-3 mt-8">
            {currentImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToView(index)}
                className={`relative transition-all duration-300 ${
                  index === activeView
                    ? "w-12 h-3"
                    : "w-3 h-3 opacity-50 hover:opacity-75"
                }`}
                aria-label={`Go to view ${index + 1}`}
              >
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    index === activeView
                      ? "bg-accent shadow-lg shadow-accent/50"
                      : "bg-border hover:bg-accent/50"
                  }`}
                />
              </button>
            ))}
          </div>

          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 dark:bg-background/60 backdrop-blur-sm border border-border/50">
              <span className="text-sm font-medium text-muted-foreground">
                View {activeView + 1} of {totalViews}:
              </span>
              <span className="text-sm font-semibold text-accent">
                {activeView === 0 && "Main Dashboard"}
                {activeView === 1 && "Assistant Menu"}
                {activeView === 2 && "Create Assistant"}
                {activeView === 3 && "Voice Library"}
                {activeView === 4 && "Voice Recorder"}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 lg:mt-20">
          <div className="text-center p-6 rounded-xl bg-background/50 backdrop-blur-sm border border-border/50 hover:border-accent/30 transition-all duration-300 hover:shadow-lg">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Easy Creation
            </h3>
            <p className="text-muted-foreground">
              Create AI assistants in minutes with our intuitive interface
            </p>
          </div>

          <div className="text-center p-6 rounded-xl bg-background/50 backdrop-blur-sm border border-border/50 hover:border-accent/30 transition-all duration-300 hover:shadow-lg">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Voice Library
            </h3>
            <p className="text-muted-foreground">
              Access a comprehensive library of voices and customize your
              assistant
            </p>
          </div>

          <div className="text-center p-6 rounded-xl bg-background/50 backdrop-blur-sm border border-border/50 hover:border-accent/30 transition-all duration-300 hover:shadow-lg">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Real-time Recording
            </h3>
            <p className="text-muted-foreground">
              Record and test voices instantly with our built-in recorder
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
